/**
 * sessionStorage-backed cache of recipe card data, used to render an
 * "optimistic" hero on the recipe detail page while the real DB queries
 * are in flight.
 *
 * Flow:
 *   1. List page renders cards with title + image + meta (already in memory).
 *   2. On hover/click, the card writes its data to sessionStorage keyed by slug.
 *   3. Recipe detail loading.tsx reads the cache and renders a real hero
 *      immediately — no skeleton flash, view-transition image morph lands on
 *      a real <img> at the correct size.
 *   4. Once the server data arrives, the real page replaces the optimistic one.
 *
 * Constraints:
 *   - Per-tab (sessionStorage), so opening a recipe in a new tab doesn't see
 *     the cache. That's fine — fresh tabs get the skeleton, like before.
 *   - 10-minute TTL so stale data doesn't linger across long sessions.
 *   - Silent failures (private mode, full storage) — the optimistic UI is a
 *     progressive enhancement; the real loading skeleton is the fallback.
 */

export interface CachedRecipeCard {
  title: string;
  imageUrl: string | null;
  description: string | null;
  prepTimeMinutes: number;
  cookTimeMinutes: number;
  servings: number;
  caloriesPerServing: number | null;
  categoryName: string | null;
}

const PREFIX = 'recipe-card:';
const TTL_MS = 10 * 60 * 1000;
// Sweep thresholds — when either is exceeded on a write, drop expired entries.
// Cache keys on slug only (no version / model invalidation), so without a
// sweep an active session would accumulate stale rows indefinitely.
const SWEEP_MAX_ENTRIES = 50;
const SWEEP_MAX_BYTES = 1024 * 1024; // 1 MB

interface Entry {
  value: CachedRecipeCard;
  expiresAt: number;
}

/**
 * Best-effort sweep of expired prefixed entries when the cache outgrows its
 * budget. Runs synchronously inside a write — keep it cheap. We only enumerate
 * keys we own (PREFIX) so we never touch other apps' sessionStorage.
 */
function maybeSweepExpired(storage: Storage): void {
  let count = 0;
  let totalBytes = 0;
  // First pass: count + size our own keys.
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (!key || !key.startsWith(PREFIX)) continue;
    count++;
    // Approximate UTF-16 byte size — sessionStorage strings are 2 bytes/char.
    const value = storage.getItem(key);
    totalBytes += (key.length + (value?.length ?? 0)) * 2;
  }

  if (count <= SWEEP_MAX_ENTRIES && totalBytes <= SWEEP_MAX_BYTES) return;

  // Second pass: collect expired keys (can't mutate during iteration above).
  const now = Date.now();
  const toRemove: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (!key || !key.startsWith(PREFIX)) continue;
    try {
      const raw = storage.getItem(key);
      if (!raw) continue;
      const entry = JSON.parse(raw) as Entry;
      if (entry.expiresAt < now) toRemove.push(key);
    } catch {
      // Malformed entry — drop it on the floor too.
      toRemove.push(key);
    }
  }
  for (const key of toRemove) storage.removeItem(key);
}

export function cacheRecipeCard(slug: string, data: CachedRecipeCard): void {
  if (typeof window === 'undefined') return;
  try {
    maybeSweepExpired(window.sessionStorage);
    const entry: Entry = { value: data, expiresAt: Date.now() + TTL_MS };
    window.sessionStorage.setItem(`${PREFIX}${slug}`, JSON.stringify(entry));
  } catch {
    // sessionStorage unavailable / full — silent fail
  }
}

export function readCachedRecipeCard(slug: string): CachedRecipeCard | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(`${PREFIX}${slug}`);
    if (!raw) return null;
    const entry = JSON.parse(raw) as Entry;
    if (entry.expiresAt < Date.now()) {
      window.sessionStorage.removeItem(`${PREFIX}${slug}`);
      return null;
    }
    return entry.value;
  } catch {
    return null;
  }
}
